import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BadgePercent } from "lucide-react";
import { api, cardProduct, offerPct } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import ProductCard from "@/components/ProductCard";
import { useSeo } from "@/lib/seo";

const MIN_OPTIONS = [50, 30, 20, 10];

export default function OffersPage() {
  const [params, setParams] = useSearchParams();
  const minOffer = Number(params.get("min")) || 10;
  const brandId = params.get("brand") || "";
  const categoryId = params.get("category") || "";
  const sort = params.get("sort") || "offer";
  const { catalog, pincodeInfo } = useStore();
  const storeId = pincodeInfo?.store_id || "";

  const [pool, setPool] = useState([]);
  const [done, setDone] = useState(false);

  useSeo({
    title: `${minOffer}% & Above Offers | Soonyoung`,
    description: `All beauty products at ${minOffer}% off and above — live offers from Soonyoung's active offers module.`,
    path: `/offers?min=${minOffer}`,
  });

  useEffect(() => {
    let cancelled = false;
    setPool([]);
    setDone(false);
    const base = { page_size: 100, ...(storeId && { store_id: storeId }) };
    (async () => {
      try {
        const first = await api.get("/ecom/catalog/products", { params: { ...base, page: 1 } });
        if (cancelled) return;
        setPool(first.data?.items || []);
        const pages = first.data?.total_pages || 1;
        await Promise.all(
          Array.from({ length: pages - 1 }, (_, i) =>
            api.get("/ecom/catalog/products", { params: { ...base, page: i + 2 } })
              .then((r) => { if (!cancelled) setPool((p) => p.concat(r.data?.items || [])); })
              .catch(() => {})
          )
        );
      } catch { /* keep whatever loaded */ }
      if (!cancelled) setDone(true);
    })();
    return () => { cancelled = true; };
  }, [storeId]);

  const patch = (obj) => {
    const next = new URLSearchParams(params);
    Object.entries(obj).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next);
  };

  const items = useMemo(() => {
    const filtered = pool.filter(
      (p) =>
        offerPct(p) >= minOffer &&
        (p.in_stock ?? true) &&
        (!brandId || p.brand_id === brandId) &&
        (!categoryId || p.category_id === categoryId)
    );
    const by = {
      offer: (a, b) => offerPct(b) - offerPct(a),
      "price-asc": (a, b) => (a.offer_price ?? a.selling_price ?? 0) - (b.offer_price ?? b.selling_price ?? 0),
      "price-desc": (a, b) => (b.offer_price ?? b.selling_price ?? 0) - (a.offer_price ?? a.selling_price ?? 0),
      name: (a, b) => (a.name || "").localeCompare(b.name || ""),
    };
    return filtered.sort(by[sort] || by.offer);
  }, [pool, minOffer, brandId, categoryId, sort]);

  const brandMap = useMemo(
    () => Object.fromEntries(catalog.brands.map((b) => [b.id, b.name])),
    [catalog.brands]
  );

  return (
    <section data-testid="offers-page" className="mx-auto max-w-7xl px-4 pb-20 pt-28 sm:px-6 sm:pt-32">
      <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-gold">
        <BadgePercent className="h-4 w-4" /> Live Offers
      </p>
      <h1 className="mt-2 font-display text-3xl tracking-tight text-cream sm:text-5xl">
        {minOffer}% <span className="italic text-gold">&amp; above</span> offers
      </h1>

      <div className="mt-6 flex gap-2 overflow-x-auto pb-2 no-scrollbar" data-testid="offers-min-chips">
        {MIN_OPTIONS.map((m) => (
          <button
            key={m}
            data-testid={`offers-min-${m}`}
            onClick={() => patch({ min: m })}
            className={`shrink-0 rounded-full px-6 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${minOffer === m ? "bg-black text-white" : "border border-champagne/50 text-sage hover:text-cream"}`}
          >
            {m}% &amp; above
          </button>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="offers-filters">
        <select
          data-testid="offers-filter-brand"
          value={brandId}
          onChange={(e) => patch({ brand: e.target.value })}
          className="rounded-full border border-champagne/50 bg-pine px-4 py-2.5 text-xs text-cream focus:border-gold focus:outline-none"
        >
          <option value="">All brands</option>
          {catalog.brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <select
          data-testid="offers-filter-category"
          value={categoryId}
          onChange={(e) => patch({ category: e.target.value })}
          className="rounded-full border border-champagne/50 bg-pine px-4 py-2.5 text-xs text-cream focus:border-gold focus:outline-none"
        >
          <option value="">All categories</option>
          {catalog.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select
          data-testid="offers-sort"
          value={sort}
          onChange={(e) => patch({ sort: e.target.value })}
          className="rounded-full border border-champagne/50 bg-pine px-4 py-2.5 text-xs text-cream focus:border-gold focus:outline-none"
        >
          <option value="offer">Offer: high to low</option>
          <option value="price-asc">Price: low to high</option>
          <option value="price-desc">Price: high to low</option>
          <option value="name">Name A–Z</option>
        </select>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fern" data-testid="offers-count">
          {done ? `${items.length} product${items.length === 1 ? "" : "s"}` : `Loading… ${items.length} so far`}
        </p>
      </div>

      {items.length === 0 && done ? (
        <div className="mt-10 flex flex-col items-center rounded-3xl border border-dashed border-champagne/60 py-20 text-center" data-testid="offers-empty">
          <p className="font-display text-2xl text-cream">No offers match these filters</p>
          <p className="mt-2 text-sm text-sage">Try a lower offer level or clear the brand/category filter.</p>
        </div>
      ) : (
        <motion.div layout className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-4" data-testid="offers-grid">
          {items.map((p) => <ProductCard key={p.id} p={cardProduct(p, brandMap[p.brand_id])} />)}
        </motion.div>
      )}
    </section>
  );
}
